import { useState, useEffect } from 'react';
import { fetchGalleryBySlug, getResizedImageUrl } from '../lib/cms';

export default function Gallery({ pageSlug }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      setLoading(true);
      const data = await fetchGalleryBySlug(pageSlug);
      setGallery(data);
      setLoading(false);
    }
    loadGallery();
  }, [pageSlug]);

  const handlePrev = (e) => {
    if (!gallery || !gallery.images) return;
    if (e && e.stopPropagation) e.stopPropagation();
    setActiveIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    if (!gallery || !gallery.images) return;
    if (e && e.stopPropagation) e.stopPropagation();
    setActiveIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e) => {
    if (activeIndex === null) return;
    if (e.key === 'ArrowLeft') handlePrev(e);
    if (e.key === 'ArrowRight') handleNext(e);
    if (e.key === 'Escape') setActiveIndex(null);
  };

  // Bind keyboard events
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, gallery]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  if (!gallery) {
    return null;
  }

  const hasImages = gallery.images && gallery.images.length > 0;

  const getAltText = (slug, index, isLightbox = false) => {
    const keywords = {
      'belaegning': 'belægning',
      'ny-indkoersel': 'ny indkørsel',
      'ny-terrasse': 'ny terrasse',
      'stoettemur': 'støttemur',
      'haveanlaeg': 'haveanlæg',
      'entreprenoer': 'entreprenør',
      'stoebearbejde': 'støbearbejde',
      'betonarbejde': 'betonarbejde',
      'murerarbejde': 'murerarbejde',
      'totalloesninger': 'totalløsninger',
      'anlaegsarbejde': 'anlægsarbejde'
    };
    const kw = keywords[slug] || 'anlægsarbejde';
    return isLightbox
      ? `Professionelt udført ${kw} af Larsen & Kristensen - Forstørret billede ${index + 1}`
      : `Eksempel på ${kw} i Skive, Holstebro og Viborg - Billede ${index + 1}`;
  };

  return (
    <div className="my-12">
      <div className="border-l-4 border-[#163A2B] pl-4 mb-6">
        <h2 className="text-2xl font-bold text-[#1C1E1D]">Projektgalleri</h2>
        <p className="text-sm text-[#1C1E1D]/70">Se eksempler på vores udførte arbejde inden for {gallery.title?.toLowerCase() || 'dette område'}.</p>
      </div>

      {hasImages ? (
        /* Image Grid */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {gallery.images.map((imgUrl, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className="group relative aspect-4/3 overflow-hidden rounded-md border border-[#E2E2E0] bg-white shadow-sm transition-all duration-300 hover:shadow-md hover:border-[#163A2B] focus:outline-none focus:ring-2 focus:ring-[#163A2B] cursor-pointer"
            >
              <img
                src={getResizedImageUrl(imgUrl, 400, 80)}
                alt={getAltText(pageSlug, idx, false)}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <span className="bg-white/90 text-[#1C1E1D] text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm flex items-center gap-1.5">
                  <i className="fa-solid fa-magnifying-glass-plus" /> Forstør
                </span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#163A2B]/10 text-[#163A2B] mb-3">
            <i className="fa-regular fa-images text-xl" />
          </div>
          <p className="text-sm font-medium text-[#1C1E1D]">Billeder er på vej</p>
          <p className="text-xs text-[#1C1E1D]/60 mt-1 max-w-md mx-auto">Vi opdaterer løbende vores galleri med billeder fra vores seneste projekter.</p>
        </div>
      )}

      {/* Lightbox Modal */}
      {activeIndex !== null && hasImages && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 sm:p-6 select-none"
          onClick={() => setActiveIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-4 right-4 z-50 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 p-3 rounded-full transition-colors focus:outline-none cursor-pointer"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark text-2xl" />
          </button>

          {/* Prev Button */}
          <button
            onClick={handlePrev}
            className="absolute left-4 z-50 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 p-4 rounded-full transition-colors focus:outline-none cursor-pointer"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left text-2xl" />
          </button>

          {/* Main Image Container */}
          <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={getResizedImageUrl(gallery.images[activeIndex], 1600, 85)}
              alt={getAltText(pageSlug, activeIndex, true)}
              className="max-h-[80vh] max-w-full object-contain rounded-sm shadow-2xl"
            />
            <div className="absolute bottom-[-35px] left-0 right-0 text-center text-white/80 text-sm font-medium">
              Billede {activeIndex + 1} af {gallery.images.length}
            </div>
          </div>

          {/* Next Button */}
          <button
            onClick={handleNext}
            className="absolute right-4 z-50 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 p-4 rounded-full transition-colors focus:outline-none cursor-pointer"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right text-2xl" />
          </button>
        </div>
      )}
    </div>
  );
}
